<script setup lang="ts">
  import { storeToRefs } from 'pinia';
  import { computed, onBeforeMount } from 'vue';
  import type { HTMLAttributes } from 'vue';
  import { useRoute } from 'vue-router';

  import type { Invoice, Partner } from '@stuut/api/models';
  import { Button } from '@ui/components/button';
  import type { ButtonVariants } from '@ui/components/button';
  import { ComboSelect } from '@ui/components/combo-select';
  import { useLoader } from '@ui/components/combo-select/useLoader';
  import { MonetaryText } from '@ui/components/monetary';

  import { useInvoices } from '@/store';

  interface Props {
    btnSize?: string;
    clearable?: boolean;
    disabled?: boolean;
    disabledFn?: (invoice: Invoice) => boolean;
    invoiceId?: string | string[];
    partner?: Partner;
    partnerId?: string;
    queryName?: string;
    showAmountOption?: boolean;
    showSelectedAmount?: boolean;
    status?: string[];
    triggerClass?: HTMLAttributes['class'];
    triggerSize?: ButtonVariants['size'];
    variant?: 'dropdown' | 'filter';
  }

  const props = withDefaults(defineProps<Props>(), {
    queryName: 'invoice',
    variant: 'dropdown',
  });

  const emit = defineEmits<{
    (e: 'update', value: Invoice | null): void;
  }>();

  const route = useRoute();
  const invoiceStore = useInvoices();
  const { invoiceSelect: invoices, selectInitLoad } = storeToRefs(invoiceStore);

  const selectedInvoiceId = computed(() => route.query?.[props.queryName] || props.invoiceId);

  const selectedInvoice = computed(
    () => invoices.value?.find((inv) => inv.id === selectedInvoiceId.value) ?? null,
  );

  const _partnerId = computed(() => props.partnerId || props.partner?.id);

  const invoiceLoader = useLoader({
    load: async (args = {}) => {
      const params = {
        ...args,
        partner: _partnerId.value,
        status: props.status,
      };

      if (!invoices.value?.length && !selectInitLoad) {
        await invoiceStore.setupInvoiceSelect({ params });
        return invoices.value;
      }

      if (args?.search || (args && args.search !== undefined)) {
        await invoiceStore.fetchInvoiceSelect({ params });
        return invoices.value;
      }

      await invoiceStore.fetchInvoiceSelect({ params });
      return invoices.value;
    },
  });

  const initiallyLoading = computed(
    () => invoiceLoader.loading.value && !invoiceLoader.initialDataLoaded.value,
  );

  onBeforeMount(async () => {
    if (!invoices.value.length) {
      await invoiceStore.setupInvoiceSelect({
        params: {
          partner: _partnerId.value,
          status: ['open'],
        },
      });
    }

    if (props.invoiceId) await invoiceStore.getPeekFindSelect(props.invoiceId);
  });

  defineExpose({
    amount_due: computed(() => selectedInvoice.value?.amount_due),
    currency: computed(() => selectedInvoice.value?.currency),
    invoices,
    selectedInvoice,
  });
</script>

<template>
  <ComboSelect
    v-model="selectedInvoice"
    :loader="invoiceLoader"
    searchable
    dropdown-class="min-w-[250px]"
    :variant="variant"
    :placeholder="variant === 'filter' ? 'Invoice' : 'Select an invoice'"
    search-placeholder="Search invoices..."
    option-identifier="number"
    :clearable="clearable"
    :disabled="disabled"
    :disabled-fn="disabledFn"
    :size="triggerSize"
    :btn-class="`flex-1 ${triggerClass} px-3.5`"
    @update:model-value="(invoice) => emit('update', invoice as Invoice | null)"
  >
    <template #filter-label>
      <template v-if="variant === 'filter'">
        <i class="fas fa-file-invoice-dollar !text-xs" />
        <span>Invoice</span>
      </template>
      <span v-else>Select an invoice</span>
    </template>

    <template #fixedBottom="{ closeSelect }">
      <Button
        v-if="clearable && selectedInvoice"
        class="text-left w-full"
        variant="ghost"
        size="sm"
        @click="
          emit('update', null);
          closeSelect();
        "
      >
        Clear selection
      </Button>
    </template>

    <template #selected="{ value }">
      <div v-if="initiallyLoading" class="truncate flex items-center">
        <i class="fa fa-spinner-third animate-spin" />
      </div>

      <template v-else>
        <div class="truncate flex items-center gap-x-6 w-full">
          <span class="truncate">{{ value?.number }}</span>

          <MonetaryText
            v-if="showSelectedAmount"
            class="truncate"
            :value="value?.total_amount"
            :currency="value?.currency"
            :compact="true"
          />
        </div>
      </template>
    </template>

    <template #option="{ option }">
      <div class="flex items-center justify-between w-full">
        <strong>{{ option?.number }}</strong>

        <slot
          name="invoice_option"
          v-bind="{
            option,
            invoice: option,
            dispute_id: option?.openDisputeId,
            dispute_item_id: option?.dispute_item_id,
            dispute_item: option?.openDisputeItem,
            in_dispute: option?.in_dispute,
            in_proposed_dispute: option?.isDisputeProposed,
            has_non_terminal_dispute: option?.hasNonTerminalDispute,
            invoices,
            disabled,
          }"
        />

        <MonetaryText
          v-if="showAmountOption"
          class="text-right"
          :value="option?.total_amount"
          :currency="option?.currency"
          :compact="true"
        />
      </div>
    </template>
  </ComboSelect>
</template>
